import { useState, useEffect, useRef, createContext, useContext } from "react";

// Global counter per page load — limits auto-popups to 2 per visit
let autoPopupCount = 0;
const MAX_AUTO_POPUPS = 2;

// Reset counter on route changes (called externally or on mount)
export const resetAutoPopupCounter = () => { autoPopupCount = 0; };

/** Context to force all FirstTimeHints to show as fresh (used in test/preview modes) */
const HintForceResetContext = createContext(false);
export const HintForceResetProvider = HintForceResetContext.Provider;

import { Info, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface FirstTimeHintProps {
  id: string;
  title: string;
  description: string;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
  iconSize?: "sm" | "md";
  /** When true, ignore localStorage and always start as "fresh" (for test/preview modes) */
  forceReset?: boolean;
}

export const STORAGE_PREFIX = "tgh_hint_seen_";

// Stage flow: "fresh" (popup auto-shows) → "blue" (dismissed popup, icon is blue) → "done" (icon is white/muted)
type HintStage = "fresh" | "blue" | "done";

const FirstTimeHint = ({ id, title, description, side = "bottom", className, iconSize = "sm", forceReset: forceResetProp }: FirstTimeHintProps) => {
  const contextForceReset = useContext(HintForceResetContext);
  const forceReset = forceResetProp || contextForceReset;
  const storageKey = `${STORAGE_PREFIX}${id}`;
  const [stage, setStage] = useState<HintStage>(() => {
    if (forceReset) return "fresh";
    try {
      const v = localStorage.getItem(storageKey);
      if (v === "2") return "done";
      if (v === "1") return "blue";
      return "fresh";
    } catch { return "fresh"; }
  });
  const [open, setOpen] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [flipAlign, setFlipAlign] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Reset to fresh when forceReset becomes true (e.g. entering test mode or switching member)
  useEffect(() => {
    if (forceReset) {
      setStage("fresh");
      setShowPopover(false);
      setOpen(false);
    }
  }, [forceReset]);

  // Check if popup would overflow left edge and flip to left-aligned if needed
  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    if (rect.left < 300) {
      setFlipAlign(true);
    }
  }, [open, showPopover]);

  // On first mount, if fresh, auto-show the popover after a short delay
  useEffect(() => {
    if (stage === "fresh") {
      if (autoPopupCount < MAX_AUTO_POPUPS) {
        autoPopupCount++;
        const t = setTimeout(() => setShowPopover(true), 600);
        return () => clearTimeout(t);
      }
    }
  }, [stage]);

  // No click-outside-to-close — user must use X or Got it button

  // Dismiss the auto-popup → move to "blue" stage
  const dismissPopover = () => {
    setShowPopover(false);
    setStage("blue");
    if (!forceReset) { try { localStorage.setItem(storageKey, "1"); } catch {} }
  };

  const toggleInfo = () => {
    if (showPopover) {
      dismissPopover();
    } else if (stage === "blue" && !open) {
      setOpen(true);
      setStage("done");
      if (!forceReset) { try { localStorage.setItem(storageKey, "2"); } catch {} }
    } else {
      setOpen((o) => !o);
    }
  };

  const isVisible = showPopover || open;
  const isBlue = stage === "fresh" || stage === "blue";

  const align = flipAlign ? "left-0" : "right-0";
  const positionClasses: Record<string, string> = {
    top: `bottom-full ${align} mb-2`,
    bottom: `top-full ${align} mt-2`,
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  };

  const arrowAlign = flipAlign ? "left-3" : "right-3";

  const arrowClasses: Record<string, string> = {
    top: `top-full ${arrowAlign} border-l-transparent border-r-transparent border-b-transparent border-t-border`,
    bottom: `bottom-full ${arrowAlign} border-l-transparent border-r-transparent border-t-transparent border-b-border`,
    left: "left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-border",
    right: "right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-border",
  };

  const iconPx = iconSize === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";

  return (
    <div ref={ref} className={cn("relative inline-flex items-center overflow-visible", className)}>
      <button
        onClick={toggleInfo}
        className={cn(
          "relative inline-flex items-center justify-center rounded-full p-0.5 transition-all duration-200 focus:outline-none hover:scale-105",
          isBlue ? "text-primary" : "text-muted-foreground",
          showPopover && "animate-pulse"
        )}
        aria-label={`Info: ${title}`}
        type="button"
      >
        {isBlue && (
          <>
            <motion.span
              aria-hidden="true"
              className="pointer-events-none absolute inset-[-4px] rounded-full bg-primary/10 blur-sm"
              animate={{
                scale: [1, 1.08, 1],
                opacity: [0.5, 0.7, 0.5],
              }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
              style={{
                boxShadow: "0 0 10px hsl(var(--primary) / 0.35)",
              }}
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-[-1px] rounded-full border border-primary/25 bg-primary/5"
            />
          </>
        )}
        <Info
          className={cn(iconPx, "relative z-10", isBlue && "drop-shadow-[0_0_4px_hsl(var(--primary)/0.6)]")}
          strokeWidth={2.5}
        />
      </button>

      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.15 }}
            onMouseDown={(e) => e.stopPropagation()}
            className={cn(
              "absolute z-50 w-64 rounded-lg border bg-popover/95 backdrop-blur-sm shadow-lg p-3",
              positionClasses[side],
              (showPopover || stage === "blue") && "ring-1 ring-primary/30"
            )}
          >
            {/* Arrow */}
            <span className={cn("absolute w-0 h-0 border-[6px]", arrowClasses[side])} />

            <div className="flex items-start justify-between gap-2 mb-1">
              <h4 className="font-heading text-xs font-bold text-foreground leading-tight">{title}</h4>
              <button
                onClick={() => showPopover ? dismissPopover() : setOpen(false)}
                className="text-muted-foreground hover:text-foreground shrink-0 -mt-0.5"
                type="button"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <p className="font-body text-[11px] text-muted-foreground leading-relaxed">{description}</p>

            {showPopover && (
              <button
                onClick={dismissPopover}
                className="mt-2 w-full text-[10px] font-heading font-bold text-primary hover:text-primary/80 transition-colors text-center py-1 rounded border border-primary/20 hover:bg-primary/5"
                type="button"
              >
                Got it
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FirstTimeHint;

/** Utility to reset all hints (for testing) */
export const resetAllHints = () => {
  Object.keys(localStorage).forEach((k) => {
    if (k.startsWith(STORAGE_PREFIX)) localStorage.removeItem(k);
  });
};
